/* Recently viewed products.

   1. Recording. When this file is loaded on a product page (/products/<handle>), the product is stored in
      localStorage under "colesice:recently-viewed" as a list of { id, handle }, newest first, at most 20.
      The id comes from ShopifyAnalytics (set by Shopify on every product page); if that is missing it is read from
      /products/<handle>.js. For the list to fill up, this file must be loaded on product pages (the section does it
      when it is on the page; the product page or layout should load it as well).

   2. Showing. <recent-products> reads that list (without the product being viewed), asks the search page for those
      products and swaps the cards into the section:
        GET /search?type=product&q=id:1 OR id:2&section_id=<section id>
      The response is this same section rendered with search results; the cards inside its [data-results] element
      are copied in and ordered newest first. If Shopify does not know the section id on the search page, the request is
      repeated with the section's file name (data-section-file). With nothing to show the section stays hidden. */

const STORAGE_KEY = 'colesice:recently-viewed';
const MAX_STORED = 20;

const readList = () => {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((item) => item && item.id && item.handle) : [];
  } catch (error) {
    return [];
  }
};

const writeList = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch (error) {
    /* storage unavailable: nothing is remembered */
  }
};

const currentHandle = () => {
  const match = /\/products\/([^/?#]+)/.exec(location.pathname);
  return match ? decodeURIComponent(match[1]) : '';
};

async function recordCurrentProduct() {
  const handle = currentHandle();
  if (!handle) return;
  let id = window.ShopifyAnalytics?.meta?.product?.id;
  if (!id) {
    try {
      const root = window.Theme?.config?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) id = (await response.json()).id;
    } catch (error) {
      return;
    }
  }
  if (!id) return;
  const list = readList().filter((item) => item.handle !== handle);
  list.unshift({ id: Number(id), handle });
  writeList(list);
}

if (!window.__recentlyViewedRecorded) {
  window.__recentlyViewedRecorded = true;
  recordCurrentProduct();
}

class RecentProducts extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('[data-results]');
    this.section = this.closest('.section');
    if (!this.results || this.dataset.loaded === 'true') return;

    const exclude = this.dataset.exclude || currentHandle();
    const limit = Number(this.dataset.limit) || 6;
    this.items = readList().filter((item) => item.handle !== exclude).slice(0, limit);
    if (!this.items.length) return; // nothing viewed yet: the section stays hidden (placeholders stay in the editor)
    this.load();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  async request(sectionId) {
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', this.items.map((item) => `id:${item.id}`).join(' OR '));
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { signal: this.abort.signal });
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const cards = [...doc.querySelectorAll('[data-results] .product-tile')];
    return cards.length ? cards : null;
  }

  async load() {
    this.abort = new AbortController();
    try {
      let cards = await this.request(this.dataset.sectionId);
      if (!cards && this.dataset.sectionFile && this.dataset.sectionFile !== this.dataset.sectionId) {
        cards = await this.request(this.dataset.sectionFile);
      }
      if (!cards) return;

      // Newest first, in the order they were viewed. A card is wrapped by nothing, so it is the item itself.
      const order = new Map(this.items.map((item, index) => [item.handle, index]));
      cards.sort((a, b) => (order.get(a.dataset.productHandle) ?? 99) - (order.get(b.dataset.productHandle) ?? 99));

      this.results.replaceChildren(...cards);
      this.dataset.loaded = 'true';
      if (this.section) this.section.hidden = false;
      window.Theme?.initAnimations?.(this);
      this.querySelector('cs-carousel')?.update?.();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Could not load recently viewed products', error);
    }
  }
}

if (!customElements.get('recent-products')) customElements.define('recent-products', RecentProducts);
